"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDownIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { useCallback } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";
export type ConversationProps = ComponentProps<typeof StickToBottom>;
export const Conversation = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex-1 overflow-y-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <StickToBottom className={t1} initial="smooth" resize="smooth" role="log" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;
export const ConversationContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-8 p-4", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <StickToBottom.Content className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type ConversationEmptyStateProps = ComponentProps<"div"> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};
export const ConversationEmptyState = t0 => {
  const $ = _c(18);
  let children;
  let className;
  let icon;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      title: t1,
      description: t2,
      icon,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const title = t1 === undefined ? "No messages yet" : t1;
  const description = t2 === undefined ? "Start a conversation to see messages here" : t2;
  let t3;
  if ($[7] !== className) {
    t3 = cn("flex size-full flex-col items-center justify-center gap-3 p-8 text-center", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== description || $[11] !== icon || $[12] !== title) {
    t4 = children ?? <>{icon && <div className="text-muted-foreground">{icon}</div>}<div className="space-y-1"><h3 className="text-sm font-medium">{title}</h3>{description && <p className="text-muted-foreground text-sm">{description}</p>}</div></>;
    $[9] = children;
    $[10] = description;
    $[11] = icon;
    $[12] = title;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== props || $[15] !== t3 || $[16] !== t4) {
    t5 = <div className={t3} {...props}>{t4}</div>;
    $[14] = props;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export type ConversationScrollButtonProps = ComponentProps<typeof Button>;
export const ConversationScrollButton = t0 => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    isAtBottom,
    scrollToBottom
  } = useStickToBottomContext();
  let t1;
  if ($[3] !== scrollToBottom) {
    t1 = () => {
      scrollToBottom();
    };
    $[3] = scrollToBottom;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleScrollToBottom = t1;
  let t2;
  if ($[5] !== className || $[6] !== handleScrollToBottom || $[7] !== isAtBottom || $[8] !== props) {
    t2 = !isAtBottom && <Button className={cn("absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full", className)} onClick={handleScrollToBottom} size="icon" type="button" variant="outline" {...props}><ArrowDownIcon className="size-4" /></Button>;
    $[5] = className;
    $[6] = handleScrollToBottom;
    $[7] = isAtBottom;
    $[8] = props;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};